// Pure status-bucket derivation for a single payment row, shared by
// PaymentCard (rendering) and its tests. Kept dependency-free (no React, no
// callApi) so it's trivially unit-testable.
//
// Priority mirrors src/hooks/useTreatmentPaymentStatus.js's derivePaymentState
// (awaiting > expired > failed > noLink) but operates on ONE payment rather
// than picking a "primary" across a list, and adds two states that hook
// doesn't need: 'received' (paid via a manual, non-link mode of payment) and
// 'refunded' (refund info present on the payment/session).
export const PAYMENT_LINK_MODES = ['EMAIL', 'SEND'];

// The Take Payment dialog's "£X received over the phone / in practice" flow
// records a payment with one of these modes — money the practice has
// ALREADY received, even when `paid_at` is still null (legacy data; the
// backend is being fixed + backfilled to set `paid_at` going forward, but
// existing rows need to read as paid today, not "Not paid yet").
export const RECEIVED_MODES = ['PHONE', 'INPRACTICE', 'BANK_TRANSFER', 'OTHER'];

function hasCheckoutSession(checkoutSummary) {
    const cs = checkoutSummary || {};
    return Boolean(cs.has_open_checkout || cs.latest_checkout_session_id || cs.latest_is_expired || cs.latest_status);
}

// Refund info shape is not yet finalised server-side (backend PR pending) —
// check several plausible field names defensively so the card degrades
// gracefully (shows 'paid'/'received' instead) rather than crashing if none
// of them are present.
export function paymentHasRefund(payment) {
    const p = payment || {};
    if (p.refund_summary && (p.refund_summary.refunded || Number(p.refund_summary.amount) > 0)) return true;
    if (p.is_refunded) return true;
    if (Number(p.refunded_amount) > 0) return true;
    return false;
}

export function derivePaymentCardStatus(payment) {
    const p = payment || {};
    const checkoutSummary = p.checkout_summary || {};

    if (p.paid_at) {
        if (paymentHasRefund(p)) return 'refunded';
        // `paid_manually` (set by the mark-paid endpoint) always renders as the
        // headline "Paid" pill — it's the "someone confirmed this money came
        // in" state, regardless of the underlying mode_of_payment. This is
        // distinct from a payment that was simply recorded as already-paid via
        // a non-link mode at creation time (still 'received' below).
        if (p.paid_manually) return 'paid';
        const mode = p.mode_of_payment;
        if (mode && !PAYMENT_LINK_MODES.includes(mode)) return 'received';
        return 'paid';
    }

    // Legacy already-received data: a manual mode was recorded (Take Payment
    // dialog) with no checkout session ever created for this payment, but
    // `paid_at` hasn't been backfilled yet. Still 'received', not 'noLink' —
    // the money is already in, it's just the timestamp that's missing (use
    // created_at as the received date — see paymentPresentation.js).
    if (!hasCheckoutSession(checkoutSummary) && RECEIVED_MODES.includes(p.mode_of_payment)) return 'received';

    if (checkoutSummary.has_open_checkout) return 'awaiting';
    if (checkoutSummary.latest_is_expired) return 'expired';
    if (checkoutSummary.latest_status === 'failed') return 'failed';
    return 'noLink';
}

export const STATUS_FILTER_ORDER = ['all', 'awaiting', 'paid', 'expired', 'failed', 'noLink', 'refunded'];

export const STATUS_FILTER_LABELS = {
    all: 'All',
    awaiting: 'Awaiting',
    paid: 'Paid',
    expired: 'Expired',
    failed: 'Failed',
    noLink: 'No link',
    refunded: 'Refunded',
};

// 'received' counts toward the "Paid" chip/summary bucket — it's still money
// collected, just not through Stripe. 'noLink' (an unpaid payment with no
// Stripe link yet) counts toward "Awaiting" — it's still money the practice
// expects, just not through a sent link — which also empties the standalone
// "No link" chip out of the filter bar (see STATUS_FILTER_ORDER usage).
export function statusFilterBucket(status) {
    if (status === 'received') return 'paid';
    if (status === 'noLink') return 'awaiting';
    return status;
}
